import { RefObject, useEffect, useState } from 'react'
import { sameSize, Size } from './videoLayout'

/**
 * The intrinsic size of the stream playing inside `containerRef`, read from the <video> element
 * the Agora SDK mounts there (`videoWidth` x `videoHeight`, i.e. after rotation is applied).
 *
 * This only OBSERVES the SDK's element; it never styles, replays or re-subscribes anything:
 *   - the `resize` event fires whenever the stream's dimensions change, which is what happens
 *     when the patient rotates their phone (portrait <-> landscape) or the sender changes
 *     resolution;
 *   - a MutationObserver re-attaches when the SDK creates or replaces its <video> element.
 * State only changes when the dimensions do.
 */
const useVideoDimensions = (
  containerRef: RefObject<HTMLElement>
): Size | null => {
  const [size, setSize] = useState<Size | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    let video: HTMLVideoElement | null = null

    const read = () => {
      const width = video ? video.videoWidth : 0
      const height = video ? video.videoHeight : 0
      // 0 x 0 means no frame yet: keep the last known size rather than flicker to "unknown".
      if (width > 0 && height > 0) {
        const next = { width, height }
        setSize((prev) => (sameSize(prev, next) ? prev : next))
      }
    }

    const detach = () => {
      if (!video) return
      video.removeEventListener('resize', read)
      video.removeEventListener('loadedmetadata', read)
      video = null
    }

    const attach = () => {
      const next = container.querySelector('video')
      if (next === video) return
      detach()
      video = next
      if (video) {
        video.addEventListener('resize', read)
        video.addEventListener('loadedmetadata', read)
        read()
      }
    }

    attach()
    const observer =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(attach)
        : null
    if (observer) {
      observer.observe(container, { childList: true, subtree: true })
    }

    return () => {
      if (observer) observer.disconnect()
      detach()
    }
  }, [containerRef])

  return size
}

export default useVideoDimensions
